import type { Flash, TransientData } from "./lib/page-rendering"
import { useEntry, useSession, useVisit } from "./lib/page-rendering"
import type { User, Workspace as HeldWorkspace } from "@/types/serializers"

// Inertia's flat shared props, read through the host binding. Pages and
// components read the binding itself (`useSession`, `useEntry`, `useVisit`,
// `usePathParams`, `useFeatureToggle` from @/lib/page-rendering); this
// module remains for the header, the workspace chrome and the layout until
// the composition task converts them, and for the test harness, which
// serves the props in this shape.

// The signed-in user as the header shows them: no email and no ids but their
// own workspace's owner id. A page's own data comes from its page props.
export type { User }

// A workspace the signed-in user holds, as WorkspaceResource serialises it:
// its owner and the owner's name, which the workspace page's title carries.
export type { HeldWorkspace }

// A workspace by its owner: the kind of principal whose pages it holds and
// that principal's id. A page knows its own from its route.
export type Workspace = Pick<HeldWorkspace, "ownerType" | "ownerId">

export type AuthenticationProps =
  | { authenticated: true; user: User }
  | { authenticated: false; user: null }

// The workspaces the signed-in user holds, for the header's menu: their
// teacher's, their student's, then each school they administer. Empty for an
// administrator with no role and for a visitor. A page's own workspace is
// never read from here; the page knows it from its route.
export type WorkspacesProps = { workspaces: HeldWorkspace[] }

// Whether an administrator is impersonating the signed-in user, who is then
// that user in every respect; the header offers the administrator the way
// back to the admin pages and shows nothing else of them.
export type ImpersonationProps = { impersonating: boolean }

export type I18nFlashMessage = { namespace: string; key: string }

export type FlashType = "success" | "alert"

export type FlashProps = { flash: Flash }

// Data true for one visit only, set by the request that performed a change
// and read by the page it redirected to; pages read it through `useTransient`.
export type TransientProps = { transient: TransientData }

export type FeatureToggleProps = { feature_toggles: string[] }

// The current URL's path parameters, keyed in camelCase (teacherId, classId)
// by the backend, as the route helpers in @/routes take them.
export type PathParamsProps = { path_params: Record<string, string> }

export type SharedProps = AuthenticationProps &
  WorkspacesProps &
  ImpersonationProps &
  FlashProps &
  TransientProps &
  FeatureToggleProps &
  PathParamsProps

export const useSharedProps = (): SharedProps => {
  const { authenticated, user, workspaces, impersonating, featureToggles } =
    useSession()
  const { flash, transient } = useVisit()
  const { pathParams } = useEntry()

  return {
    ...({ authenticated, user } as AuthenticationProps),
    workspaces,
    impersonating,
    flash,
    transient,
    feature_toggles: featureToggles,
    path_params: pathParams,
  }
}

export {
  useFeatureToggle,
  usePathParam,
  usePathParams,
} from "./lib/page-rendering"
